/**
 * From a Roll request to a roll plan: what the derived sheet knows about the check or damage
 * (modifiers, MAP variants, range, notes, fortune, substitutions), where the Roll came from, and
 * what committing it does to the Session (ammunition fired, an Effect ended after the roll, the
 * recovery check's outcome). Pure over the sheet and the Session, so the page only rolls and shows.
 */
import type { Character } from "../character";
import type { DerivedSheet, ModifierView, SpellView, SpellcastingEntryView, StatisticView, StrikeView } from "../engine";
import { SAVES } from "../engine/values";
import type { CheckChoices, CheckKind, CheckLine, CheckResult, CheckSpec, RollOrigin } from "../rolls";
import { DEFAULT_CHOICES, effectsEndedByRoll } from "../rolls";
import type { Session, TableAction } from "../session";
import type { RollRequest } from "./rolls";

export type RollPlan =
  | {
      kind: "check";
      label: string;
      origin: RollOrigin;
      spec: CheckSpec;
      choices: CheckChoices;
      /** A damage Roll to offer from the result (a strike, a spell attack). */
      damage: { request: RollRequest; hasCritical: boolean } | null;
      /** The ammunition the strike fires on commit, when a Session holds the stack. */
      ammunition: { id: string; name: string } | null;
      /** What the Roll consumes once made, whatever its outcome: ammunition fired, an Effect ended after the roll. */
      consumes: (result: CheckResult, session: Session) => TableAction[];
      /** What the Roll's final outcome does to the Session (a recovery check's degree); a Hero Point reroll replaces it. */
      outcome: (result: CheckResult) => TableAction[];
      /** A follow-up Roll the panel offers after this one (the flat check that ends persistent damage). */
      follow: { label: string; request: RollRequest } | null;
    }
  | {
      kind: "damage";
      label: string;
      origin: RollOrigin;
      formula: string;
      criticalFormula: string | null;
      critical: boolean;
      /** The engine's breakdown for the rolled formula, one line per part. */
      breakdown: string[];
      /** "Apply to self" is offered as `damage`, `healing` or not at all. */
      apply: "damage" | "healing" | null;
      /** The source's roll options the IWR read when the damage is taken (#22): `item:magical`, `item:trait:holy`, `item:type:spell`. */
      options: string[];
      follow: { label: string; request: RollRequest } | null;
    }
  | { kind: "inline"; label: string; origin: RollOrigin; formula: string; flavor: string | null }
  | { kind: "unavailable"; label: string; reason: string };

function lineOf(m: ModifierView): CheckLine {
  return { slug: m.slug, label: m.label, type: m.type, value: m.value, enabled: m.enabled, source: m.source, sourceName: m.sourceName, removeAfterRoll: m.removeAfterRoll };
}

function extrasOf(view: Pick<StatisticView, "notes" | "degreeAdjustments" | "rollTwice" | "rollTwiceSources" | "substitutions">): Pick<CheckSpec, "notes" | "degreeAdjustments" | "rollTwice" | "rollTwiceSources" | "substitutions"> {
  return {
    notes: view.notes.filter((n) => n.enabled && n.visibility !== "gm").map((n) => ({ title: n.title, text: n.text, outcome: [...n.outcome], source: n.source, sourceName: n.sourceName, ...(n.postRoll ? { predicate: n.predicate } : {}) })),
    degreeAdjustments: view.degreeAdjustments.filter((a) => a.enabled).map((a) => ({ adjustments: a.adjustments, source: a.source, sourceName: a.sourceName, ...(a.postRoll ? { predicate: a.predicate } : {}) })),
    rollTwice: view.rollTwice,
    rollTwiceSources: view.rollTwiceSources.filter((r) => r.enabled).map((r) => ({ keep: r.keep, source: r.source, sourceName: r.sourceName, removeAfterRoll: r.removeAfterRoll })),
    substitutions: view.substitutions.filter((s) => s.enabled).map((s) => ({ slug: s.slug, label: s.label, value: s.value, required: s.required, effectType: s.effectType, source: s.source, sourceName: s.sourceName, removeAfterRoll: s.removeAfterRoll })),
  };
}

/** The sheet statistic an inline check type names, and the check kind it rolls as. */
export function statisticFor(sheet: DerivedSheet, slug: string): { view: StatisticView; kind: CheckKind } | null {
  const skill = sheet.skills[slug];
  if (skill) return { view: skill, kind: "skill-check" };
  if ((SAVES as readonly string[]).includes(slug)) return { view: sheet.saves[slug as (typeof SAVES)[number]], kind: "saving-throw" };
  if (slug === "perception") return { view: sheet.perception, kind: "perception-check" };
  if (slug === "initiative") return { view: sheet.initiative, kind: "initiative" };
  const special = sheet.specialStatistics[slug];
  if (special) return { view: special, kind: "check" };
  const classDC = sheet.classDCs[slug];
  if (classDC) return { view: classDC, kind: "check" };
  if (slug === "class" && sheet.classDC) return { view: sheet.classDC, kind: "check" };
  for (const entry of sheet.spellcasting) if (entry.statistic && (slug === `spell-attack:${entry.id}` || slug === entry.id)) return { view: entry.statistic, kind: "attack-roll" };
  return null;
}

/** A check spec from a sheet statistic. */
export function checkSpecFromStatistic(view: StatisticView, kind: CheckKind, options: { label?: string; dc?: number | null; dcLabel?: string | null; traits?: string[] } = {}): CheckSpec {
  return {
    label: options.label ?? view.label,
    kind,
    statistic: view.slug,
    lines: view.modifiers.map(lineOf),
    override: view.override ? view.override.value : null,
    dc: options.dc ?? null,
    dcLabel: options.dcLabel ?? null,
    ...extrasOf(view),
    traits: options.traits ?? [],
    map: kind === "attack-roll" ? { label: "Multiple Attack Penalty", penalties: [-5, -10] } : null,
    range: null,
  };
}

/** A flat check: no modifier at all. */
export function flatCheckSpec(label: string, dc: number | null, dcLabel: string | null = null): CheckSpec {
  return { label, kind: "flat-check", statistic: null, lines: [], override: null, dc, dcLabel, rollTwice: false, rollTwiceSources: [], substitutions: [], degreeAdjustments: [], notes: [], traits: [], map: null, range: null };
}

/** The volley distance a trait like `volley-30` names. */
function volleyOf(traits: string[]): number | null {
  const trait = traits.find((t) => /^volley-\d+$/.test(t));
  return trait ? Number(trait.slice("volley-".length)) : null;
}

/** A check spec from a Strike: the attack's modifiers, the MAP variants, the range data. */
export function checkSpecFromStrike(sheet: DerivedSheet, strike: StrikeView): CheckSpec {
  const options = new Set(strike.domains.flatMap((d) => sheet.rollOptions[d] ?? []).concat(sheet.rollOptions.all ?? []));
  const ranged = strike.ranged || strike.thrown;
  return {
    label: strike.label,
    kind: "attack-roll",
    statistic: strike.overrideKey,
    lines: strike.modifiers.map(lineOf),
    override: strike.override ? strike.override.value : null,
    dc: null,
    dcLabel: "the target's AC",
    ...extrasOf(strike),
    traits: [...strike.actionTraits, ...strike.traits],
    map: { label: strike.map.label, penalties: [strike.variants[1].penalty, strike.variants[2].penalty] },
    range: ranged ? { increment: strike.rangeIncrement, max: strike.maxRange, volley: volleyOf(strike.traits), ignoreRangePenalty: options.has("ignore-range-penalty") } : null,
  };
}

function findStrike(sheet: DerivedSheet, key: string): StrikeView | null {
  return sheet.strikes.flatMap((s) => [s, ...s.altUsages]).find((s) => s.overrideKey === key) ?? null;
}

function findSpell(sheet: DerivedSheet, entryId: string, uuid: string): { entry: SpellcastingEntryView<StatisticView>; spell: SpellView } | null {
  const entry = sheet.spellcasting.find((e) => e.id === entryId);
  const spell = entry?.spells.find((s) => s.uuid === uuid) ?? (entry ? Object.values(entry.prepared).flat().find((s) => s?.uuid === uuid) : null);
  return entry && spell ? { entry, spell } : null;
}

/** A Strike's damage as the IWR see it (#22): a weapon (or unarmed attack) with its traits, magical when it carries a rune or the trait. */
function strikeDamageOptions(strike: StrikeView): string[] {
  const magical = strike.runes.potency > 0 || strike.runes.striking > 0 || strike.runes.property.length > 0 || strike.traits.includes("magical");
  return [strike.traits.includes("unarmed") ? "item:category:unarmed" : "item:type:weapon", ...strike.traits.map((t) => `item:trait:${t}`), ...(magical ? ["item:magical"] : [])];
}

/** A spell's damage as the IWR see it (#22): magical, a spell, with its traits and its Entry's tradition. */
function spellDamageOptions(entry: SpellcastingEntryView<StatisticView>, spell: SpellView): string[] {
  return ["item:type:spell", "item:magical", ...spell.traits.map((t) => `item:trait:${t}`), ...(entry.tradition ? [`item:trait:${entry.tradition}`, `origin:action:trait:${entry.tradition}`] : [])];
}

/** The Effects a check ends after the roll, as Session actions. */
function endedEffects(spec: CheckSpec, result: CheckResult, session: Session): TableAction[] {
  return effectsEndedByRoll(spec, result).flatMap((e) => {
    const effect = session.effects.find((s) => s.uuid === e.source);
    return effect ? [{ kind: "end-effect", id: effect.id } as const] : [];
  });
}

/** The Foundry formula behind a `@Damage` enricher, or one from its words when the raw formula is missing. */
function damageFormulaOf(request: Extract<RollRequest, { kind: "damage" }>): string | null {
  if (request.unresolved.length > 0) return null;
  if (request.raw) return request.raw;
  const instances = request.formula.split(",").map((part) => {
    const words = part.trim().split(/\s+/);
    const tags: string[] = [];
    while (words.length > 1 && /^[a-z-]+$/.test(words[words.length - 1]!)) tags.unshift(words.pop()!);
    return tags.length > 0 ? `(${words.join(" ")})[${tags.join(",")}]` : words.join(" ");
  });
  return `{${instances.join(",")}}`;
}

/** Turn a request into a plan over the sheet and the Session. */
export function planRoll(sheet: DerivedSheet, character: Pick<Character, "session">, request: RollRequest): RollPlan {
  const session = character.session;
  switch (request.kind) {
    case "check": {
      if (request.type === "flat") {
        return { kind: "check", label: request.label, origin: { kind: "entry", key: null, name: request.name }, spec: flatCheckSpec(request.label, request.dc, request.against ? `the target's ${request.against}` : null), choices: DEFAULT_CHOICES, damage: null, ammunition: null, consumes: () => [], outcome: () => [], follow: null };
      }
      const found = request.statistic ? statisticFor(sheet, request.statistic) : null;
      if (!found) return { kind: "unavailable", label: request.label, reason: `The Character has no ${request.type} statistic to roll.` };
      const spec = checkSpecFromStatistic(found.view, found.kind, { label: request.label, dc: request.dc, dcLabel: request.against ? `the target's ${request.against}` : null, traits: request.traits });
      return { kind: "check", label: request.label, origin: { kind: "statistic", key: found.view.slug, name: found.view.label }, spec, choices: DEFAULT_CHOICES, damage: null, ammunition: null, consumes: (result, s) => endedEffects(spec, result, s), outcome: () => [], follow: null };
    }
    case "damage": {
      const formula = damageFormulaOf(request);
      if (!formula) return { kind: "unavailable", label: request.label, reason: `${request.unresolved.join(", ")} could not be resolved from the sheet.` };
      return { kind: "damage", label: request.label, origin: { kind: "entry", key: null, name: request.name }, formula, criticalFormula: null, critical: false, breakdown: [], apply: /\bhealing\b/.test(formula) ? "healing" : "damage", options: [...request.traits.map((t) => `item:trait:${t}`), ...request.options], follow: null };
    }
    case "inline":
      return { kind: "inline", label: request.label, origin: { kind: "inline", key: null, name: null }, formula: request.formula, flavor: request.flavor };
    case "strike": {
      const strike = findStrike(sheet, request.strikeKey);
      if (!strike) return { kind: "unavailable", label: request.label, reason: "The Strike is no longer on the sheet." };
      const spec = checkSpecFromStrike(sheet, strike);
      const stack = strike.ammunition?.selected && session ? (session.consumables.find((c) => c.id === strike.ammunition!.selected) ?? null) : null;
      const ammunition = stack ? { id: stack.id, name: stack.name } : null;
      const damage = strike.damage ? { request: { kind: "strike-damage" as const, strikeKey: strike.overrideKey, label: `${strike.label} damage`, formula: strike.damage.formula.success ?? "", critical: strike.damage.formula.criticalSuccess }, hasCritical: !!strike.damage.formula.criticalSuccess } : null;
      return {
        kind: "check",
        label: strike.label,
        origin: { kind: "strike", key: strike.overrideKey, name: strike.label },
        spec,
        choices: { ...DEFAULT_CHOICES, map: request.map, rangeIncrement: spec.range ? 1 : null },
        damage,
        ammunition,
        consumes: (result, s) => [...(ammunition && s.consumables.some((c) => c.id === ammunition.id && c.quantity > 0) ? [{ kind: "fire-ammunition" as const, id: ammunition.id }] : []), ...endedEffects(spec, result, s)],
        outcome: () => [],
        follow: null,
      };
    }
    case "strike-damage": {
      const strike = findStrike(sheet, request.strikeKey);
      if (!strike?.damage) return { kind: "unavailable", label: request.label, reason: "The Strike has no damage formula." };
      const critical = request.asCritical === true && !!strike.damage.formula.criticalSuccess;
      const formula = critical ? strike.damage.formula.criticalSuccess! : strike.damage.formula.success;
      if (!formula) return { kind: "unavailable", label: request.label, reason: "The Strike has no damage formula." };
      return { kind: "damage", label: `${strike.label} damage`, origin: { kind: "strike", key: strike.overrideKey, name: strike.label }, formula, criticalFormula: strike.damage.formula.criticalSuccess, critical, breakdown: critical ? strike.damage.breakdown.criticalSuccess : strike.damage.breakdown.success, apply: "damage", options: strikeDamageOptions(strike), follow: null };
    }
    case "spell-attack": {
      const found = findSpell(sheet, request.entryId, request.spellUuid);
      if (!found?.entry.statistic) return { kind: "unavailable", label: request.label, reason: "The spell has no attack statistic on the sheet." };
      const spec = checkSpecFromStatistic(found.entry.statistic, "attack-roll", { label: request.label, dcLabel: "the target's AC", traits: found.spell.traits });
      const damage = found.spell.damage?.formula.success ? { request: { kind: "spell-damage" as const, entryId: request.entryId, spellUuid: request.spellUuid, label: `${found.spell.name} damage` }, hasCritical: !!found.spell.damage.formula.criticalSuccess } : null;
      return { kind: "check", label: request.label, origin: { kind: "spell", key: request.spellUuid, name: found.spell.name }, spec, choices: { ...DEFAULT_CHOICES, map: request.map }, damage, ammunition: null, consumes: (result, s) => endedEffects(spec, result, s), outcome: () => [], follow: null };
    }
    case "spell-damage": {
      const found = findSpell(sheet, request.entryId, request.spellUuid);
      const damage = found?.spell.damage;
      if (!damage?.formula.success) return { kind: "unavailable", label: request.label, reason: "The spell has no damage at this rank." };
      const critical = request.asCritical === true && !!damage.formula.criticalSuccess;
      const formula = critical ? damage.formula.criticalSuccess! : damage.formula.success;
      return { kind: "damage", label: `${found!.spell.name} damage`, origin: { kind: "spell", key: request.spellUuid, name: found!.spell.name }, formula, criticalFormula: damage.formula.criticalSuccess, critical, breakdown: critical ? damage.breakdown.criticalSuccess : damage.breakdown.success, apply: damage.kinds.includes("healing") ? "healing" : "damage", options: spellDamageOptions(found!.entry, found!.spell), follow: null };
    }
    case "recovery": {
      const dying = session?.conditions.find((c) => c.slug === "dying");
      if (!session || !dying || (dying.value ?? 0) === 0) return { kind: "unavailable", label: "Recovery check", reason: "The Character is not dying." };
      const dc = sheet.death.dying.recoveryDC + (dying.value ?? 0);
      return { kind: "check", label: "Recovery check", origin: { kind: "session", key: dying.id, name: "Dying" }, spec: flatCheckSpec("Recovery check", dc, null), choices: DEFAULT_CHOICES, damage: null, ammunition: null, consumes: () => [], outcome: (result) => (result.degree ? [{ kind: "dying-recovery", degree: result.degree.value }] : []), follow: null };
    }
    case "persistent-damage": {
      const condition = session?.conditions.find((c) => c.id === request.conditionId);
      if (!condition?.persistent) return { kind: "unavailable", label: "Persistent damage", reason: "The Condition is no longer on the Session." };
      const p = condition.persistent;
      const label = `Persistent ${p.damageType} damage`;
      return { kind: "damage", label, origin: { kind: "session", key: condition.id, name: condition.name }, formula: `{(${p.formula})[${p.damageType}]}`, criticalFormula: null, critical: false, breakdown: [`${p.formula} ${p.damageType}`], apply: "damage", options: ["damage:category:persistent"], follow: { label: `Flat check to end it (DC ${p.dc})`, request: { kind: "persistent-check", conditionId: condition.id } } };
    }
    case "persistent-check": {
      const condition = session?.conditions.find((c) => c.id === request.conditionId);
      if (!condition?.persistent) return { kind: "unavailable", label: "Persistent damage", reason: "The Condition is no longer on the Session." };
      const id = condition.id;
      return { kind: "check", label: `Persistent ${condition.persistent.damageType} damage ends?`, origin: { kind: "session", key: id, name: condition.name }, spec: flatCheckSpec("Flat check", condition.persistent.dc, null), choices: DEFAULT_CHOICES, damage: null, ammunition: null, consumes: () => [], outcome: (result) => (result.degree && (result.degree.value === "success" || result.degree.value === "criticalSuccess") ? [{ kind: "clear-condition", id }] : []), follow: null };
    }
    case "fast-healing":
      return { kind: "damage", label: request.label, origin: { kind: "session", key: null, name: request.label }, formula: `{(${request.formula})[healing]}`, criticalFormula: null, critical: false, breakdown: [`${request.formula} healing`], apply: "healing", options: [], follow: null };
  }
}

/** The request a statistic panel's Roll button makes for the panel's key, or null when the key is not rolled. */
export function requestForKey(sheet: DerivedSheet, key: string, label: string): RollRequest | null {
  if (key.startsWith("strike:")) return findStrike(sheet, key) ? { kind: "strike", strikeKey: key, label, map: 0, modifier: findStrike(sheet, key)!.value } : null;
  const found = statisticFor(sheet, key);
  if (!found || key === "ac" || key.startsWith("class-dc")) return null;
  return { kind: "check", type: key, statistic: found.view.slug, label, modifier: found.view.value, dc: null, against: null, basic: false, traits: [], options: [], name: null };
}
